<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  BUZZ_SCORE_BASELINE,
  clampScore,
  scorePercentile,
} from './buzzScoreDistribution';

const props = defineProps({
  score: {
    type: Number,
    default: null,
  },
  peerScores: {
    type: Array,
    default: () => [],
  },
});

const { t } = useI18n();

const width = 640;
const height = 88;
const padding = { left: 8, right: 8, top: 24, bottom: 24 };

const displayScore = computed(() => clampScore(props.score));
const peerCount = computed(
  () =>
    props.peerScores.filter(
      value => value != null && !Number.isNaN(Number(value))
    ).length
);
const percentile = computed(() =>
  scorePercentile(displayScore.value, props.peerScores)
);

const chart = computed(() => {
  const innerWidth = width - padding.left - padding.right;
  const trackY = padding.top + 16;
  const baselineX = padding.left + (BUZZ_SCORE_BASELINE / 100) * innerWidth;
  const markerX =
    displayScore.value == null
      ? null
      : padding.left + (displayScore.value / 100) * innerWidth;

  return { innerWidth, trackY, baselineX, markerX };
});

const competitionLabel = computed(() => {
  if (
    displayScore.value == null ||
    peerCount.value < 2 ||
    percentile.value == null
  ) {
    return '';
  }
  return t('BUZZ_REPORTS.SCALE.PERCENTILE', { percentile: percentile.value });
});

const baselineLabel = computed(() =>
  t('BUZZ_REPORTS.SCALE.BASELINE', { score: BUZZ_SCORE_BASELINE })
);
</script>

<template>
  <div v-if="displayScore == null" class="text-sm text-n-slate-10">
    {{ t('BUZZ_REPORTS.SCALE.NO_SCORE') }}
  </div>
  <template v-else>
    <svg
      class="w-full h-24 text-n-teal-9"
      :viewBox="`0 0 ${width} ${height}`"
      role="img"
    >
      <title>{{ t('BUZZ_REPORTS.SCALE.TITLE') }}</title>
      <rect
        :x="padding.left"
        :y="chart.trackY - 4"
        :width="chart.innerWidth"
        height="8"
        rx="4"
        class="fill-n-alpha-2"
      />
      <rect
        :x="padding.left"
        :y="chart.trackY - 4"
        :width="chart.baselineX - padding.left"
        height="8"
        rx="4"
        class="fill-n-teal-9/20"
      />
      <line
        :x1="chart.baselineX"
        :x2="chart.baselineX"
        :y1="padding.top"
        :y2="height - padding.bottom"
        class="stroke-n-slate-8"
        stroke-width="1"
      />
      <g v-if="chart.markerX != null">
        <text
          :x="chart.markerX"
          :y="padding.top - 2"
          text-anchor="middle"
          class="fill-n-teal-11"
          font-size="11"
          font-weight="600"
        >
          {{ displayScore }}
        </text>
        <circle
          :cx="chart.markerX"
          :cy="chart.trackY"
          r="6"
          class="fill-n-teal-9 stroke-n-solid-2"
          stroke-width="2"
        />
      </g>
      <text
        :x="padding.left"
        :y="height - 4"
        class="fill-n-slate-10"
        font-size="10"
      >
        0
      </text>
      <text
        :x="chart.baselineX"
        :y="height - 4"
        text-anchor="middle"
        class="fill-n-slate-10"
        font-size="10"
      >
        {{ t('BUZZ_REPORTS.SCALE.BASELINE', { score: BUZZ_SCORE_BASELINE }) }}
      </text>
      <text
        :x="width - padding.right"
        :y="height - 4"
        text-anchor="end"
        class="fill-n-slate-10"
        font-size="10"
      >
        100
      </text>
    </svg>
    <p class="text-xs text-n-slate-11 mb-0 mt-2">
      {{ baselineLabel }}
      <span v-if="competitionLabel"> · {{ competitionLabel }}</span>
    </p>
  </template>
</template>
